import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../../css/pie-wheel.css';

const easeOutQuad = t => t * (2 - t);

function generateRandomColors(length) {
  const colors = [];
  for (let i = 0; i < length; i++) {
    const hue = Math.floor(Math.random() * 360);
    const sat = 70 + Math.floor(Math.random() * 20);
    const light = 40 + Math.floor(Math.random() * 20);
    colors.push(`hsl(${hue}, ${sat}%, ${light}%)`);
  }
  return colors;
}

const PieWheel = ({
  entries = [],
  winner,
  onComplete,
  duration = 15000,
  numRotations = 6,
  startPause = 3000,
  finalPause = 3000
}) => {
  const wheelRef = useRef(null);
  const [rotationAngle, setRotationAngle] = useState(0);

  const numSegments = entries.length || 1;
  const anglePerSegment = 360 / numSegments;
  const sliceSkew = anglePerSegment - 90;

  const sliceColors = useMemo(() => generateRandomColors(numSegments), [numSegments]);

  // Find winner index
  const normalizedWinner = winner?.username?.trim().toLowerCase() || '';
  const winnerIndex = useMemo(() => {
    const idx = entries.findIndex(e =>
      e.username.trim().toLowerCase() === normalizedWinner
    );
    return idx >= 0 ? idx : 0;
  }, [entries, normalizedWinner]);

  // Rotation to bring winner to top
  const segmentCenterAngle = winnerIndex * anglePerSegment + anglePerSegment / 2;
  const targetRotation = numRotations * 360 + (360 - segmentCenterAngle);

  // Slight random offset
  const randomOffset = useMemo(() => {
    const variation = anglePerSegment / 5;
    return (Math.random() - 0.5) * 2 * variation;
  }, [anglePerSegment]);

  const finalRotation = targetRotation + randomOffset;

  // Spin animation
  useEffect(() => {
    let startTime = null;
    let animFrame;
    let finishTimeout;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuad(progress);
      const current = finalRotation * eased;
      setRotationAngle(current);

      if (progress < 1) {
        animFrame = requestAnimationFrame(animate);
      } else {
        finishTimeout = setTimeout(() => {
          onComplete && onComplete();
        }, finalPause);
      }
    };

    const startTimeout = setTimeout(() => {
      animFrame = requestAnimationFrame(animate);
    }, startPause);

    return () => {
      clearTimeout(startTimeout);
      clearTimeout(finishTimeout);
      cancelAnimationFrame(animFrame);
    };
  }, [finalRotation, duration, startPause, finalPause, onComplete]);

  return (
    <div className="piewheel-container">
      <div
        className="piewheel"
        ref={wheelRef}
        style={{ transform: `rotate(${rotationAngle}deg)` }}
      >
        {entries.map((entry, index) => {
          const wedgeAngle = index * anglePerSegment;
          const wedgeColor = sliceColors[index];

          return (
            <div
              key={index}
              className="piewheel-segment"
              style={{
                backgroundColor: wedgeColor,
                transform: `rotate(${wedgeAngle}deg) skewY(${sliceSkew}deg)`
              }}
            >
              <div
                className="piewheel-content"
                style={{
                  /*
                    1) rotate(-wedgeAngle) => undo wedge rotation
                    2) skewY(-sliceSkew)  => undo wedge skew
                    3) rotate(90deg)      => vertical text, bottom near center
                    4) translateY(-50%)   => shift outward from center
                  */
                  transform: `
                    rotate(${-wedgeAngle}deg)
                    skewY(${-sliceSkew}deg)
                    rotate(90deg)
                    translateY(-50%)
                  `
                }}
              >
                <div className="piewheel-label-group">
                  {entry.avatar && (
                    <img
                      src={entry.avatar}
                      alt={entry.username}
                      className="piewheel-avatar"
                    />
                  )}
                  <span
                    className="piewheel-label"
                    style={{ color: entry.color || '#fff' }}
                  >
                    {entry.username}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="piewheel-pointer"></div>
    </div>
  );
};

export default PieWheel;
